// Player/VisualStoryPlayer: MUI Box/styled → div + inline styles + CSS.
// Container-query responsive padding handled via @container rule.

import { navButtonsSichtbar } from './navButtonsGate';
import type { CustomFonts, PostJson, SlideOptionsSerialized, SlideSerialized } from "@scrolly/post-types";
import {
    resolveBoxEffects,
    resolveCta,
    resolveGlobalLook,
    resolveSzenenGrundfarbe,
    resolveNavButtons,
    alleRunningCtas,
    ctaAmEnde,
    tintBackgroundForBlur,
    linkRegeln,
    linkStilAus,
    linkStilVariablen,
} from "@scrolly/post-types";
import type { CtaAbgabe, CtaConfig } from "@scrolly/post-types";
import CtaButton from "./CtaButton";
import RunningCtaStapel from "./RunningCtaStapel";
import { KapitelPunkte } from "./KapitelPunkte";
import { useStoryPhase } from "./useStoryPhase";
import Renderer from "./Renderer";
import GlobalLookOverlay from "./GlobalLookOverlay";
import Credits from "./Credits";
import { ueberlagerungMassstabStil } from "./ueberlagerungMassstab";
import type { RefObject } from "react";
import { useCallback, useEffect, useRef, useSyncExternalStore } from "react";
import type VisualStoryStore from "./VisualStoryStore";
import { responsiveMultiplier } from "./calculateSlidePosition";
// viewerFont removed — the .slide-container CSS rule now inherits its
// font-family from the branding/custom font rather than hardcoding
// SofiaSansSemiCondensed, which caused a brief FOUC on re-renders.
import { getColorString } from "@scrolly/renderer";
import MarkdownViewer from "./MarkdownViewer";
import { textboxLeer } from "../utils/textboxLeer";
import { WebGLAssets } from "./webglAssets";

interface Props {
    post: PostJson;
    showQuality: boolean;
    visualStoryStore: VisualStoryStore | null;
    canvasContainerRef: RefObject<HTMLDivElement | null>;
    htmlContainerRef: RefObject<HTMLDivElement | null>;
    mainContainerRef: RefObject<HTMLDivElement | null>;
    currentSlideIndex: number;
    fontFamilies: CustomFonts;
    /**
     * Plan-2 iOS-Fix: window/body-scroll-Mode. Der Body scrollt, der
     * Renderer-Div klebt per sticky am Viewport (Hoehe 100lvh) und loest sich
     * am Artikelende. DEFAULT false (container, heute).
     */
    bodyScroll?: boolean;
    /**
     * Mitlaufender CTA IN der Buehne verankern (sticky/fixed-Stage) statt
     * position:fixed am Fenster. Der oeffentliche Voll-Reader braucht das:
     * seine Huelle (Phone-Frame-Shell) macht einen Vorfahren zum
     * Bezugsrahmen, fixed klebte dadurch am ARTIKEL und scrollte mit dem
     * Inhalt davon (Jans Fund 08.08.2026, per DOM-Messung: Streifen bei
     * y=-5958). Exakt dieselbe Bauform wie der Editor-Platz — nur ohne
     * editorMode-Verhalten (Knopf klickbar).
     */
    runningCtaImRahmen?: boolean;
    /**
     * Maßstab für CTA und Nachweis IM RAHMEN (nur mit `runningCtaImRahmen`),
     * wenn die Bühne kleiner ist als ein Handy: Bühnenbreite / Handybreite.
     * Die Galerie-Kachel setzt ihn für Bild-Vorlagen (Jans Fund 05.10.2026,
     * Begründung in ueberlagerungMassstab.ts). Fehlt er → unverändert.
     */
    ueberlagerungMassstab?: number;
    /**
     * Rückmeldung einer VOTE-/RATING-Abgabe. NUR die öffentliche Leserseite
     * setzt sie, und nur bei echten Lesern (kein `?preview=1`, kein OG-Bild).
     * Fehlt sie, ist das eine Vorschau: Die Abstimmung ist bedienbar, aber
     * nichts wird gemerkt oder gemeldet (Vorbereitung Stufe 2, 27.09.2026).
     */
    onCtaAntwort?: (abgabe: CtaAbgabe) => void;
    /**
     * Editor-Modus: reicht an <Renderer> durch, damit der HTML-Overlay-Container
     * pointer-events:none bekommt (sonst schluckt er Gizmo-/Layer-Klicks). Reader
     * lässt das undefined → unverändertes Verhalten.
     */
    editorMode?: boolean;
    /** Standard-Bezeichnung der Kapitel-Punkte in der Sprache des Lesers („Kapitel“/„Chapter“) */
    kapitelWort?: string;
}

const dummy = () => {
    return () => {};
};
const dummySnapShot = () => 800;

/**
 * Resolve the `.slide-content` textbox background. Handles three cases:
 *
 *   1. No color and no blur → undefined (fully transparent).
 *   2. No color but blur on → legacy white-translucent fallback so
 *      older PPI templates that rely on blur-only still render.
 *   3. Color set, blur off → solid `rgba(r,g,b,a)` from the color.
 *   4. Color set, blur on → same color BUT alpha capped at 0.85 so the
 *      backdrop-filter blur is visible behind the tint. Capping only
 *      kicks in when the authored alpha is higher than the cap — users
 *      who picked a subtler tint (e.g. 0.2) keep their exact choice.
 */
/**
 * True when a slide's text is visually empty. The editor stores rich text, so
 * a "cleared" box is rarely the exact string "" — it is usually markup that
 * renders nothing: `<p></p>`, `<p><br></p>`, `&nbsp;`, or pure whitespace.
 * The old check (`text === ""`) missed all of those, so an emptied text box
 * still rendered its tinted background box. (Laura, 2026-07-28)
 *
 * Eingebetteter Inhalt (iframe/video/img …) zaehlt mit — s. utils/textboxLeer
 * (Jans Fund 23.09.2026: ein Kasten mit NUR einer eingebetteten Seite hat
 * keine Buchstaben und wurde deshalb gar nicht gebaut).
 */
const isSlideTextEmpty = textboxLeer;

// tintBackgroundForBlur lebt jetzt in @scrolly/post-types (box-effects.ts) —
// dieselbe Regel gilt auch fuer die Pro-Editor-Buehne (TextOverlayNormal).

/**
 * NAV-BUTTONS: die zwei runden Weiter/Zurück-Knöpfe unten rechts — Jans
 * Legacy-Feature (NavigationControls, 1.0), portiert 09.08.2026. Neu dabei:
 * Die Pfeile zeigen HOCH/RUNTER statt links/rechts und stehen übereinander —
 * ein Scrolly läuft vertikal. Farben aus den Metadaten (resolveNavButtons).
 *
 * Sichtbarkeits-Regeln:
 *   - nur im Querformat und ab Tablet-Kurzseite (navButtonsGate.ts; Gate beim
 *     Aufrufer, der die BÜHNEN-Maße kennt — dadurch stimmt es auch im
 *     Geräterahmen der Previews)
 *   - nicht vor dem Start (Cover) und nicht hinter der Story (Credits) —
 *     weich per Opacity, wie das Reveal im Original.
 */
// Tastatur-Navigation (Jans Auftrag 30.08.2026): Bei mehreren Playern auf
// einer Seite (Editor-Bühne + geöffnete Vorschau, Preview-Kacheln unter
// My Scrollys) darf ein Pfeiltasten-Druck nicht ALLE gleichzeitig schalten.
// Regel: nur die ZULETZT gemountete Nav-Spalte reagiert — das ist die
// oberste Ansicht (z.B. der gerade geöffnete Vorschau-Dialog).
const navTastaturStapel: symbol[] = [];

// Export nur für die Phasen-Tests (app/src/lib/__tests__/nav-buttons-phasen)
// — im Produkt rendert die Spalte ausschließlich der Player selbst.
export function NavButtonsSpalte({
    store,
    arrowColor,
    bgColor,
}: {
    store: VisualStoryStore;
    arrowColor: string;
    bgColor: string;
    /** Nicht mehr ausgewertet (Ziele kommen aus utils/navZiel) — bleibt fuer Aufrufer. */
    slideCount?: number;
    currentSlideIndex?: number;
}) {
    // Drei Phasen statt eines booleschen imLauf (Jans Auftrag 30.08.2026):
    // VOR der Story sind beide Knöpfe aus, IM Lauf beide an, und HINTER der
    // Story (Credits) bleibt der HOCH-Pfeil sichtbar — sonst gibt es am Ende
    // keinen Knopf zurück in die Story und man muss von Hand scrollen.
    const phase = useStoryPhase(store);


    const geheZu = (richtung: "hoch" | "runter") => {
        if (richtung === "hoch") {
            // Am ersten Slide fuehrt „hoch" zurueck zum Start (Cover) — Jans
            // Fund 22.09.2026; vorher war der Pfeil dort gesperrt.
            store.previousSlide();
        } else {
            // Hinter der letzten Slide geht es zu den Credits (Regel aus Jans
            // 1.0) — das entscheidet nextSlide selbst anhand der Ziel-
            // positionen (utils/navZiel). Frueher hier ueber currentSlideIndex,
            // der aus derselben ungenauen Kasten-Zuordnung kam und am
            // vorletzten textlosen Slide schon „letzte" melden konnte.
            store.nextSlide();
        }
    };

    // Pfeil LINKS/RECHTS schalten die Slides — wie die Knöpfe, auch in den
    // Vorschauen (Jans Auftrag 30.08.2026). Bewusst die horizontalen Pfeile:
    // hoch/runter scrollen die Seite bzw. die Story selbst. Der Stapel oben
    // sorgt dafür, dass nur die oberste Nav-Spalte reagiert; Eingabefelder
    // (Editor-Formulare!) behalten ihre Pfeiltasten.
    const tastaturId = useRef<symbol | null>(null);
    if (!tastaturId.current) tastaturId.current = Symbol("nav-tastatur");
    useEffect(() => {
        const ich = tastaturId.current!;
        navTastaturStapel.push(ich);
        return () => {
            const i = navTastaturStapel.indexOf(ich);
            if (i >= 0) navTastaturStapel.splice(i, 1);
        };
    }, []);
    useEffect(() => {
        const aufTaste = (e: KeyboardEvent) => {
            if (e.key !== "ArrowLeft" && e.key !== "ArrowRight") return;
            if (e.metaKey || e.ctrlKey || e.altKey || e.shiftKey) return;
            if (navTastaturStapel[navTastaturStapel.length - 1] !== tastaturId.current) return;
            const ziel = e.target as HTMLElement | null;
            if (
                ziel &&
                (ziel.tagName === "INPUT" ||
                    ziel.tagName === "TEXTAREA" ||
                    ziel.tagName === "SELECT" ||
                    ziel.isContentEditable)
            ) {
                return;
            }
            // dieselbe Sichtbarkeitsregel wie die Knöpfe: vor dem Start aus;
            // hinter der Story (Credits) geht nur noch ZURÜCK (Hoch-Pfeil)
            if (phase === "vor") return;
            if (phase === "nach" && e.key === "ArrowRight") return;
            e.preventDefault();
            // stopPropagation, damit der Pro-Editor die Taste nicht doppelt
            // verarbeitet (sein keymaster bindet left/right an den Timeline-
            // Abspielkopf) — s. Capture-Begründung unten.
            e.stopPropagation();
            geheZu(e.key === "ArrowLeft" ? "hoch" : "runter");
        };
        // CAPTURE-Phase, nicht bubble (Jans Fund 30.08.2026: „geht überall,
        // nur nicht im Pro Editor"): Der Pro-Editor bindet left/right via
        // keymaster (document-Ebene) an den Timeline-Abspielkopf, und dessen
        // `return false` stoppt die Propagation — ein bubble-Listener am
        // window kam bei offener Vorschau nie an die Taste. Capture am window
        // ist die ERSTE Station im Ereignisweg und greift davor. Ist keine
        // Nav-Spalte gemountet (Vorschau zu, Editor-Bühne), existiert dieser
        // Listener nicht und die Timeline-Kürzel arbeiten unverändert.
        window.addEventListener("keydown", aufTaste, true);
        return () => window.removeEventListener("keydown", aufTaste, true);
    });

    const knopf = (richtung: "hoch" | "runter") => {
        // Hinter der Story (Credits) gibt es nichts mehr „runter" — der
        // Runter-Pfeil blendet dort aus, nur der Hoch-Pfeil bleibt (Jans
        // Auftrag 30.08.2026: am Ende per Knopf zurück in die Story).
        const versteckt = richtung === "runter" && phase === "nach";
        return (
            <button
                type="button"
                aria-label={richtung === "hoch" ? "Vorherige Slide" : "Nächste Slide"}
                onClick={() => {
                    if (versteckt) return;
                    geheZu(richtung);
                }}
                style={{
                    width: 48,
                    height: 48,
                    borderRadius: "50%",
                    border: "none",
                    padding: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    background: bgColor,
                    boxShadow: "0 2px 10px rgba(0, 0, 0, 0.25)",
                    cursor: versteckt ? "default" : "pointer",
                    opacity: versteckt ? 0 : 1,
                    pointerEvents: versteckt ? "none" : undefined,
                    transition: "opacity 0.3s ease",
                }}
            >
                <svg
                    width="22"
                    height="22"
                    viewBox="0 0 24 24"
                    style={{ transform: richtung === "hoch" ? "rotate(180deg)" : undefined }}
                >
                    {/* Chevron nach UNTEN; „hoch" dreht ihn um. Duenner
                        Strich wie die Pfeile in Jans 1.0 („edler", 09.08.). */}
                    <path
                        d="M5 9 L12 16 L19 9"
                        fill="none"
                        stroke={arrowColor}
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    />
                </svg>
            </button>
        );
    };

    return (
        <div
            style={{
                position: "absolute",
                // 12 statt 24 (Jans Feedback 09.08.2026: „noch zu weit weg").
                right: 12,
                bottom: 12,
                display: "flex",
                flexDirection: "column",
                gap: 12,
                // Spalte sichtbar im Lauf UND hinter der Story (dort zeigt
                // sie nur noch den Hoch-Pfeil, s. knopf/versteckt) — aus ist
                // sie allein VOR dem Start (Cover).
                pointerEvents: phase !== "vor" ? "auto" : "none",
                opacity: phase !== "vor" ? 1 : 0,
                transition: "opacity 0.3s ease",
            }}
        >
            {knopf("hoch")}
            {knopf("runter")}
        </div>
    );
}

export default function VisualStoryPlayer({
    canvasContainerRef,
    htmlContainerRef,
    visualStoryStore,
    post,
    mainContainerRef,
    currentSlideIndex,
    fontFamilies,
    bodyScroll = false,
    editorMode = false,
    kapitelWort,
    runningCtaImRahmen = false,
    ueberlagerungMassstab,
    onCtaAntwort,
}: Props) {
    // isAfterStory-Subscription entfernt 2026-05-28 — der einzige Konsument
    // war der spacer-Toggle (Z. 242), der jetzt konstant stableHeight ist.
    // afterStorySubscribers im Store bleibt — könnte später wieder gebraucht
    // werden, kostet nichts.
    const slides = post.data.data.slides;

    // Positions-Neuvermessung, wenn Textboxen GEBOREN werden oder
    // verschwinden (Jans Fund 07.08.2026): Der Store vermisst die
    // .slide-container-Rechtecke beim Aufbau — tippt man im Template-Editor
    // Text in ein leer exportiertes Dokument, mountet die Box zwar, blieb
    // aber bis zum Reload unsichtbar zusammengefaltet (Vermessung von der
    // leeren Box). Die Signatur reagiert nur auf leer<->gefuellt und
    // show-Wechsel, nicht auf jeden Tastendruck. Doppel-Lauf nach 10 ms wie
    // im debouncedUpdate des Stores (textWidth stabilisiert sich erst nach
    // dem ersten Durchgang). Im Leser aendert sich die Signatur nie.
    const textBoxSignatur = (slides ?? [])
        .map((s: SlideSerialized | null) =>
            s && (s.text?.show ?? true) && !isSlideTextEmpty(s.text?.text) ? "1" : "0",
        )
        .join("");
    // ZEIT-RASTER in die Signatur (Jans Tempo-Regler 13.10.2026): die
    // Sektionshoehen skalieren mit den from-Abstaenden (animateAreaHeight,
    // factor = Spanne/1000) — aendert der Regler das Raster, muessen die
    // Positionen NEU vermessen werden, sonst scrollt die Vorschau bis zum
    // Reload auf dem alten Stand.
    const rasterSignatur = (slides ?? [])
        .map((s: SlideSerialized | null) => s?.from ?? 0)
        .join(",");
    useEffect(() => {
        if (!visualStoryStore) return undefined;
        visualStoryStore.regenerateSlidePositionsCache();
        const t = setTimeout(() => visualStoryStore.regenerateSlidePositionsCache(), 10);
        return () => clearTimeout(t);
    }, [textBoxSignatur, rasterSignatur, visualStoryStore]);

    const innerHeight = useSyncExternalStore<number>(
        (visualStoryStore && visualStoryStore.subscribeToHeightChanges) || dummy,
        (visualStoryStore && visualStoryStore.heightSnapshot) || dummySnapShot,
        (visualStoryStore && visualStoryStore.heightSnapshot) || dummySnapShot,
    );

    const innerWidth = useSyncExternalStore<number>(
        (visualStoryStore && visualStoryStore.subscribeToWidthChanges) || dummy,
        (visualStoryStore && visualStoryStore.widthSnapshot) || dummySnapShot,
        (visualStoryStore && visualStoryStore.widthSnapshot) || dummySnapShot,
    );

    const currentSlide: SlideSerialized | undefined = slides[currentSlideIndex];
    // A-4 (Code-Review 2026-05-29): optional-chain MUST continue through
    // `credits` — the SlideSerialized type declares it non-optional, but at
    // runtime any slide that bypasses the import adapter (legacy/hand-edited
    // DB row, future editor/Studio output) can lack the field entirely.
    // The previous `?.credits.light` stopped the chain at `currentSlide`, so
    // a credits-less slide threw and crashed the whole (shared) renderer tree
    // → white-screen. Line 87 already used the safe pattern; 85/86 did not.
    // Die TITEL-Slide (Startbildschirm, `from === 0`) traegt ihren eigenen
    // Foto-Nachweis in `titleCredits` — sie hat ein eigenes Hintergrundbild.
    // Regel 1:1 aus Jans Original (components/Credits):
    // `slide.from === 0 ? slide.titleCredits : slide.credits`. Ohne diesen Fall
    // blieb der Nachweis des Startbildschirms unsichtbar, obwohl das Feld im
    // Editor gepflegt wurde (Jans Fund). Fallback auf `credits`, damit Posts
    // ohne `titleCredits` (Studio/Alt-Bestand) unveraendert bleiben.
    const activeCredits =
        currentSlide?.from === 0 && currentSlide?.titleCredits?.text
            ? currentSlide.titleCredits
            : currentSlide?.credits;
    const creditsLight = activeCredits?.light;
    const creditsText = activeCredits?.text;
    const creditsPhotographerUrl = activeCredits?.photographerUrl;
    const creditsWeitere = (activeCredits as { weitere?: Array<{ text: string; photographerUrl?: string }> } | undefined)?.weitere;

    // Use innerHeight directly so the sticky renderer div updates on viewport changes.
    // innerHeight only changes on container resize (not during scroll), so no scroll-jump issues.
    const stableHeight = innerHeight;

    // Use the slide background color as the canvas background. The SVG
    // background rect is always stripped transparent at runtime, so this
    // CSS background provides the chart's "canvas color". Handles both
    // camelCase (editor-normalized) and snake_case (Studio raw JSON).
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const presOpts = (post.data.data.presentationOptions ?? (post.data.data as any).presentation_options) as any;
    const slideOpts = presOpts?.slideOptions ?? presOpts?.slide_options;
    const bgc = slideOpts?.backgroundColor ?? slideOpts?.background_color;
    const readerBg: string = bgc && typeof bgc.r === 'number'
        ? `rgb(${bgc.r}, ${bgc.g}, ${bgc.b})`
        : "transparent";
    // Im EDITOR liegt hinter dem Canvas die Buehne, nicht die Lese-Seite. Die
    // Flaechenbreite ist dort meist krumm (z. B. 866,664 px unter Interface-Zoom),
    // sodass die letzte Pixelspalte nur teilweise vom Canvas gedeckt wird und die
    // helle Flaeche dahinter als 1px-Rand durchscheint — das sind auch die weissen
    // Blitzlinien am Rand. Deckend schwarz wie in Jans 1.0; Reader unveraendert.
    // GRUNDFARBE DER SZENE (Jan 19.09.2026): Editor, Vorschau und Reader
    // zeigen jetzt DIESELBE Farbe hinter den Ebenen. Bis dahin stand hier im
    // Editor fest "#000" — Jans halbtransparentes Hintergrundbild sah dadurch
    // im Editor dunkelgrau und veroeffentlicht fast weiss aus. Der READER
    // bleibt fuer Bestand byte-gleich: nur eine bewusst gesetzte Szenenfarbe
    // ersetzt `readerBg` (Rangfolge in post-types/szenen-grundfarbe.ts).
    const grundfarbe = resolveSzenenGrundfarbe(presOpts);
    const scrollyBg: string = WebGLAssets.editorMode
        ? grundfarbe.css
        : grundfarbe.quelle === "szene"
          ? grundfarbe.css
          : readerBg;

    /**
     * Globaler Farb-/Film-Look der Buehne (GLOBAL-Reiter im Pro-Editor).
     *
     * Bis 05.08.2026 zeichnete ihn AUSSCHLIESSLICH die Pro-Editor-Huelle, als
     * CSS ueber ihrer Vorschau. Hier — also in Template-Editor-Vorschau und im
     * veroeffentlichten Scrolly — kam er nie an: die Werte waren gespeichert
     * und nirgends zu sehen (Jans Fund: „uebernimmt nicht die Pro-
     * Einstellungen aus GLOBAL, zum Beispiel Saettigung").
     */
    const look = resolveGlobalLook(presOpts);

    /**
     * MITLAUFENDER CTA (Jans Auftrag 05.08.2026): Fläche + Knopf ab Slide X,
     * unten am Bild, bis zum Ende. Aufbereitung und Sichtbarkeits-Regel liegen
     * in post-types/running-cta.ts — hier wird nur gezeichnet, an denselben
     * zwei Plätzen wie der Foto-Nachweis (Editor: in der Bühne / Leser: am
     * Fenster, Begründung an den Blöcken unten).
     */
    // Beide Schreibweisen wie bei `slideOptions` eine Zeile drüber — je nach
    // Backend-Pfad kommt das JSON camelCase oder snake_case an.
    // Seit 27.09.2026 MEHRERE (Liste `runningCtas` + Altbestand `runningCta`),
    // s. alleRunningCtas.
    const runningCtas = alleRunningCtas(presOpts);
    const runningCta = runningCtas.length > 0 ? runningCtas : null;
    /**
     * NACHLAUF AM ENDE (Jans prod-Test 03.10.2026, Postkarte mit Reaktion auf
     * der letzten Slide: „nur kurz zu sehen und dann schiebt sich der Footer
     * schon darüber"). Steht auf der letzten Slide ein CTA, bekommt der Leser
     * dort einen halben Bildschirm mehr Weg, bevor sich die Bühne löst und der
     * Footer kommt. Die Zeit hängt an den Slide-Abschnitten (VisualStoryStore
     * slidePositions) — der Nachlauf dahinter verschiebt keine Slide. Ohne CTA
     * am Ende bleibt alles wie bisher.
     */
    const endeVerweilen =
        !editorMode && ctaAmEnde(runningCtas, slides.length) ? Math.round(stableHeight / 2) : 0;

    /**
     * NAV-BUTTONS (Legacy-Feature, portiert 09.08.2026): Farben + An/Aus aus
     * den METADATEN (wie Titel und Vorhang; der Pro-Editor spiegelt seine
     * Startbildschirm-Felder beim Speichern dorthin).
     *
     * Größen-Gate: NUR im Querformat (Benedikt 25.09.2026; ersetzt Jans
     * „überall außer Smartphone" vom 09.08.2026). Regel und Schwellen in
     * navButtonsGate.ts — gemessen an der BÜHNE (innerWidth/innerHeight des
     * Stores, nicht am Fenster), damit es im Geräterahmen der Previews
     * genauso gilt wie im Voll-Reader.
     */
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const navButtons = resolveNavButtons((post.data as any)?.metadata as Record<string, unknown>);
    const navButtonsAn =
        navButtons !== null &&
        !editorMode &&
        visualStoryStore !== null &&
        navButtonsSichtbar(innerWidth, innerHeight);

    /**
     * WANN die mitlaufenden CTAs kommen und gehen (Jans iPhone-Funde
     * 27.09.2026: „kommen zu früh", „per NAV-Knopf kommt der CTA hinterher"):
     * Der Stapel liest die ZEIT selbst, in jedem Bild, und fährt die CTAs am
     * Scrollen entlang herein und hinaus (post-types `ctaAuftritt`). Leser und
     * Editor teilen dieselbe Quelle, den SlidePlayer: im Leser setzt der
     * Scroll-Store die Zeit, im Editor spiegelt der Adapter den Playhead hinein
     * (die Vorschau scrollt dort nie, `currentSlideIndex` bliebe 0).
     */
    const ctaZeitQuelle = useCallback(() => {
        const sp = visualStoryStore?.post?.slidePlayer;
        if (!sp) return null;
        return { zeit: sp.timer, zeiten: sp.slideTimings ?? [] };
    }, [visualStoryStore]);

    // Branded base font-size for the DOM slide-content typography.
    // Written by applyBrandingToPostJson from
    // branding.textStyles.body.fontSize → slideOptions.fontBaseSize.
    // If unset we fall back to the historical 18px.
    // Reuse the camelCase/snake_case-safe `slideOpts` resolved above instead of
    // re-dereferencing `presentationOptions.slideOptions` — the old direct deref
    // threw on a presentationOptions-less scrolly (same A-22 class as the
    // per-slide fallback further down).
    const rawFontBaseSize = (slideOpts as { fontBaseSize?: unknown } | undefined)?.fontBaseSize;
    const baseFontSize =
        typeof rawFontBaseSize === "number" && Number.isFinite(rawFontBaseSize) && rawFontBaseSize > 0
            ? rawFontBaseSize
            : 18;

    // FLIESSTEXT-GROESSE VEREINHEITLICHEN (Laura/WESER-KURIER 2026-08-18:
    // "Immer wieder variiert das Branding (Schriftart und -groesse) je nach
    // Slide").
    //
    // Ursache: `.slide-content p` hat KEINE Groessenangabe und erbt damit die
    // Grundgroesse (1em), `.slide-content h5` steht auf 1.25em. Traegt derselbe
    // Scrolly seinen Fliesstext mal in <p> und mal in <h5>, springt er zwischen
    // den Slides um 25 %. Die Regel weiter unten gibt h4-h6 bereits die BODY-
    // Schrift (weil sie erklaertermassen Fliesstext tragen), laesst ihnen aber
    // UEBERSCHRIFTEN-Groesse — dieser Widerspruch ist der Sprung.
    //
    // Bewusst NICHT global normalisiert: von 975 Scrollys mit Text nutzen nur
    // 15 ueberhaupt h4-h6, und 7 davon durchgehend — die sehen heute einheitlich
    // aus und wuerden durch eine pauschale Regel um 20 % schrumpfen. Deshalb
    // greift die Angleichung NUR, wenn das Dokument wirklich mischt (8 Faelle,
    // 6 davon WESER-KURIER). Uneinheitliche Dokumente ziehen dabei auf die
    // <p>-Groesse — das ist in diesen Dokumenten die Mehrheitsform.
    const mischtFliesstext = (() => {
        let hatP = false;
        let hatKleineUeberschrift = false;
        for (const slide of slides ?? []) {
            // Das Markup liegt unter slide.text.text — `slide.text` ist ein
            // Objekt { show, text }. Der String-Fall bleibt als Fallback fuer
            // aeltere Dokumente stehen.
            const roh = (slide as { text?: unknown } | undefined)?.text;
            const text =
                typeof roh === "string"
                    ? roh
                    : typeof (roh as { text?: unknown } | undefined)?.text === "string"
                      ? ((roh as { text: string }).text)
                      : "";
            if (text.length === 0) continue;
            if (!hatP && /<p[\s>]/i.test(text)) hatP = true;
            if (!hatKleineUeberschrift && /<h[4-6][\s>]/i.test(text)) hatKleineUeberschrift = true;
            if (hatP && hatKleineUeberschrift) return true;
        }
        return false;
    })();

    // Benedikts Regel (2026-08-03): der Branding-/Mapping-Font wird per
    // `!important` NUR bei Template-/Studio-Scrollys erzwungen. Pro-Editor-
    // Scrollys (kein templateStructure) behalten ihre im Editor bewusst
    // gesetzten Inline-Schriften — dort KEIN `!important`, nur die
    // `.slide-container`-Basis-Schrift kaskadiert auf un-ausgezeichneten Text,
    // und ein Inline-`font-family` gewinnt. Template-Format erkennt der Reader
    // an der `templateStructure` (Pro-Editor speichert ohne diese).
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const isTemplateFormat = !!(post.data as any).templateStructure;

    return (
        <>
            {/* Inject slide-content typography styles. Base font-size
                comes from applied branding (fontBaseSize, written by
                applyBrandingToPostJson) with a 18px fallback. All
                descendants use em/% so shrinking this rescales the
                whole slide-content block proportionally.

                BODY FONT OVERRIDE — Templates ship with baked `font-family`
                inline styles on <span> / <font> descendants, and — more
                importantly — many templates render body text inside
                <h5> elements (or h4/h6) instead of <p>. So the heading
                rule must be scoped to h1/h2/h3 only. h4..h6 get the
                body font with !important like everything else inside
                .slide-content. */}
            <style>{`
                ${(() => {
                    // Use the template's custom font if set, otherwise fall back
                    // to SofiaSansSemiCondensed (the platform's default renderer font).
                    // Multi-word font names MUST be quoted in CSS — without quotes
                    // "Open Sans" is parsed as two invalid generic families and the
                    // browser silently falls back to the default font.
                    const textFont = fontFamilies.textFontFamily
                        ? `'${fontFamilies.textFontFamily}', sans-serif`
                        : "'SofiaSansSemiCondensed', 'DM Sans', sans-serif";
                    // Set the SAME font on .slide-container (not the old
                    // font-shorthand which hardcoded SofiaSansSemiCondensed).
                    // This prevents a FOUC when re-renders briefly show the
                    // base font before !important rules kick in.
                    // Template/Studio: Branding-Font per !important auf ALLES in
                    // .slide-content erzwingen (schlaegt eingebackene Inline-Fonts,
                    // deckt Body-Text in h4–h6 ab). Pro-Editor: KEIN !important —
                    // die .slide-container-Basis-Schrift kaskadiert auf
                    // un-ausgezeichneten Text, bewusst gesetzte Inline-Fonts
                    // gewinnen. (Benedikts Regel, s. isTemplateFormat oben.)
                    // Gewaehlter Schnitt des Fliesstexts (nur wenn ausdruecklich
                    // gewaehlt, s. FontMappingEintrag.schnittGewaehlt) — als Basis
                    // OHNE !important: <strong> fettet relativ dazu weiter.
                    const textWeight = fontFamilies.textFontWeight
                        ? `font-weight: ${fontFamilies.textFontWeight};`
                        : "";
                    return `.slide-container {
                               position: relative;
                               z-index: 1000;
                               font-size: ${baseFontSize}px;
                               line-height: 1.4;
                               font-family: ${textFont};
                               ${textWeight}
                           }
                           ${isTemplateFormat ? `.slide-content,
                           .slide-content *,
                           .slide-content h4,
                           .slide-content h4 *,
                           .slide-content h5,
                           .slide-content h5 *,
                           .slide-content h6,
                           .slide-content h6 * {
                               font-family: ${textFont} !important;
                           }` : ""}
                           /* AUSNAHME fuer den Call-to-Action: Die Regel
                              darueber zwingt in Template-Scrollys die
                              Branding-Schrift auf ALLES in .slide-content und
                              schlaegt mit !important auch die am Button
                              gewaehlte Schrift. Der Knopf ist aber ein bewusst
                              gestaltetes Element, keine Fliesstext-Zeile — wer
                              dort eine Schrift waehlt, meint sie auch (Jans
                              Fund 05.08.2026). Ohne gewaehlte Schrift faellt
                              inherit auf die Branding-Schrift zurueck, die
                              Ausnahme kostet also nichts.
                              ACHTUNG: In diesem Block NIEMALS Backticks
                              verwenden — sie beenden den Template-String. */
                           .slide-content .scrolly-cta,
                           .slide-content .scrolly-cta * {
                               font-family: var(--scrolly-cta-font, inherit) !important;
                           }`;
                })()}
                /* Frueher @container viewer-root-container (max-width:850px).
                   Container-Queries brauchen container-type am Host — und
                   JEDES Containment am Scroller oder seinen Huellen laesst
                   WebKits Compositor das Scroll-Maximum an der Sticky-Grenze
                   des Canvas festnageln (Safari: Ende unerreichbar, Ruecksprung
                   ohne JS-Schreiber; Jans ?probe-Messreihe 27.08.2026, betraf
                   Reader UND alle Editor-Previews). Der Player kennt seine
                   Breite selbst (innerWidth aus dem Store) und setzt unten
                   .viewer-schmal — ganz ohne Containment. */
                .viewer-schmal .slide-container { padding: 0 8px; }
                .slide-content {
                    position: relative;
                    overflow-wrap: break-word;
                    hyphens: auto;
                    /* Trenn-Schwelle (Laura/WK 2026-09-08, "mobil sehr viele
                       Trennstriche", z.B. "un-sere", "Ge-schichten"): nur
                       Woerter ab 8 Buchstaben, mindestens 4 davor und 4
                       danach. Safari kennt nur die -webkit-Einzelwerte,
                       Chrome/Edge die Kurzform. */
                    -webkit-hyphenate-limit-before: 4;
                    -webkit-hyphenate-limit-after: 4;
                    hyphenate-limit-chars: 8 4 4;
                }
                /* Ueberschriften ohne automatische Trennung — ein Trennstrich
                   in der Headline ("Ge-schichten") liest sich als Fehler;
                   manuelle weiche Trennzeichen (&shy;) bleiben moeglich. */
                .slide-content h1, .slide-content h2, .slide-content h3, .slide-content h4 {
                    hyphens: manual;
                }
                .slide-content h1, .slide-content h2, .slide-content h3,
                .slide-content h1 *, .slide-content h2 *, .slide-content h3 * {
                    ${isTemplateFormat && fontFamilies.headlineFontFamily ? `font-family: '${fontFamilies.headlineFontFamily}', sans-serif !important;` : ""}
                    line-height: 1.2;
                }
                /* Pro-Editor-Format: Headline-Vorgabe aus dem fontMapping als
                   BASIS-Regel ohne !important und NUR auf den h-Elementen
                   selbst — Kinder erben, aber bewusst gesetzte Inline-Fonts
                   und <font>-Tags des Autors gewinnen (Benedikts Regel).
                   Ohne diese Regel verlor ein per Umwandeln entstandener
                   Scrolly seine Ueberschriften-Schrift: die !important-Regel
                   darueber greift nur bei templateStructure (Jans Fund
                   24.08.2026, Georgia fehlte nach Template→Pro-Umwandlung). */
                ${!isTemplateFormat && fontFamilies.headlineFontFamily ? `.slide-content h1, .slide-content h2, .slide-content h3 {
                    font-family: '${fontFamilies.headlineFontFamily}', sans-serif;
                }` : ""}
                /* Schnitt der Ueberschriften (Schriftwahl mit zwei Spalten,
                   06.10.2026): der ausdruecklich gewaehlte, sonst fett — wie der
                   Browser im Leser (der public-Reader hat keinen Reset). Im
                   App-Stack setzt der Tailwind-Preflight Ueberschriften auf
                   inherit; die Vorschau zeigte sie deshalb normal, der Leser
                   fett (Jan 06.10.2026: „Ja, wie im Leser“). Auf den
                   h-Elementen selbst, ohne !important: Hervorhebungen darin
                   (<strong>) fetten relativ dazu weiter. */
                .slide-content h1, .slide-content h2, .slide-content h3 {
                    font-weight: ${fontFamilies.headlineFontWeight ?? 700};
                }
                .slide-content h1 { font-size: 1.375em; }
                .slide-content h2 { font-size: 1.325em; }
                .slide-content h3 { font-size: 1.3em; line-height: 1.2; }
                .slide-content h4 { font-size: 1.275em; }
                .slide-content h5 { font-size: 1.25em; }
                /* h4-h6 tragen in Templates FLIESSTEXT (s. Body-Font-Regel
                   oben). Im app-Stack macht der Tailwind-Preflight Headings
                   ohnehin normalgewichtig und randlos — der public-Reader hat
                   diesen Reset NICHT, dort galt der Browser-Default (fett +
                   Aussenabstand). Lauras WK-Fund 31.08.2026: "der text ist
                   fett, obwohl im editor alles normal formatiert ist".
                   Explizit angleichen, damit alle Hosts dasselbe zeigen;
                   im app-Stack ist die Regel ein No-op. */
                .slide-content h4,
                .slide-content h5,
                .slide-content h6 {
                    font-weight: ${fontFamilies.textFontWeight ?? "normal"};
                    margin: 0;
                }
                ${mischtFliesstext ? `.slide-content h4,
                .slide-content h5,
                .slide-content h6 { font-size: 1em; }` : ""}
                .slide-content p {
                    margin: 0;
                }
                /* Leere Absaetze (Pro-Editor/Tiptap speichert eine Leerzeile als
                   <p></p>) haetten ohne Aussenabstand keine Hoehe — die getippte
                   Leerzeile verschwand im Viewer (Jans Fund 16.09.2026). Ein
                   Null-Breite-Leerzeichen gibt ihnen eine Zeilenhoehe, wie sie
                   der Template-Editor per <p><br></p> ohnehin bekommt. */
                .slide-content p:empty::before {
                    content: "\\200B";
                }
                /* ZEILENABSTAND WIE IM TEXT-EDITOR (Jan 17.09.2026): Der Pro-Editor
                   legt Fliesstext in h4–h6 und die Groesse als Spanne darum. Die
                   eigene Groesse des Blocks (1.25–1.275em) bestimmte sonst die
                   Zeilenhoehe — der im Editor gesetzte Abstand (z. B. 1,15) wirkte
                   um ein Viertel zu weit. Traegt der Block eine Groessen-Spanne,
                   steht er selbst auf 1em; Dokumente ohne solche Spannen (Alt-
                   bestand, Templates) bleiben unberuehrt. */
                .slide-content :is(h4, h5, h6):has(span[style*="font-size"]) { font-size: 1em; }
                /* LINK-DESIGN (Jan 06.10.2026): Farbe, besucht, Mouse-over und
                   Unterstreichung aus slideOptions.linkStyle (Variablen an der
                   Box), ohne Wahl Textfarbe + Unterstrich — vorher zeigte der
                   Leser Browser-Blau/Lila, die Vorschau Textfarbe. */
                ${linkRegeln(".slide-content")}
                /* ZITAT (Lauras Fund 19.09.2026: "die formatierung als zitat
                   scheint nicht zu funktionieren", links Editor-Vorschau mit
                   Balken und Kursive, rechts die geteilte Vorschau ohne alles).
                   Der Editor legt ein Zitat als
                   <blockquote class="markdownEditorTheme__quote"> ab — die
                   Klasse kommt aus Lexicals Theme und wird beim Speichern
                   mitgeschrieben. Gestaltet wurde sie aber NUR in der
                   Editor-CSS des app-Stacks (EditorTheme.css); der
                   public-Reader und das Widget laden die nicht, dort blieb der
                   Browser-Standard uebrig. Gleiche Sorte Wirt-gegen-Wirt-
                   Unterschied wie bei h4-h6 weiter oben — also steht die Optik
                   ab jetzt hier, einmal fuer alle Wirte, am ELEMENT statt an
                   der Klasse (greift damit auch fuer den Altbestand und fuer
                   Zitate aus dem Pro-Editor). Farbe bleibt geerbt: ein fester
                   Grauton wuerde in dunklen Textboxen verschwinden. */
                .slide-content blockquote {
                    margin: 0;
                    padding: 0.2em 0 0.2em 0.7em;
                    border-left: 3px solid #e5417a;
                    font-style: italic;
                }
                .slide-content font[size="-3"] { font-size: 0.4em; }
                .slide-content font[size="-2"] { font-size: 0.6em; }
                .slide-content font[size="-1"] { font-size: 0.8em; }
                .slide-content font[size="+1"] { font-size: 1.2em; }
                .slide-content font[size="+2"] { font-size: 1.4em; }
                .slide-content font[size="+3"] { font-size: 1.6em; }
            `}</style>

            <div style={{ position: "relative", background: scrollyBg }} ref={mainContainerRef}>
                <div
                    style={{ position: "relative", width: "100%" }}
                    className={
                        "article-container" +
                        (innerWidth <= 850 ? " viewer-schmal" : "")
                    }
                >
                    {/* MITLAUFENDER CTA, READER-IM-RAHMEN-Platz (oeffentlicher
                        Voll-Reader): position:fixed bricht in dessen Huelle
                        (Vorfahr wird Bezugsrahmen, Jans Fund 08.08.2026), und
                        IN der sticky-Buehne laege der Streifen UNTER den
                        HTML-Textboxen — deren slide-container (z-1000) stehen
                        als Geschwister ueber dem sticky-Kontext (z-998), und
                        ein innerer z-Index entkommt seinem Kontext nie (Jans
                        Fund 09.08.2026: „CTA muss immer ueber allem liegen").
                        Deshalb eine EIGENE Ebene als Geschwister im Artikel,
                        z-1201 schlaegt slide-container (1000), Buehne (998)
                        und Nachweis (1200) im selben Kontext.
                        AM ENDE MIT DER BUEHNE HOCH (Jans prod-Test 03.10.2026:
                        „dann schiebt sich der Footer schon darueber"): Bis
                        dahin war das eine Haft-Ebene mit Hoehe 0 — die klebt,
                        bis der GANZE Artikel vorbei ist. Die Buehne loest sich
                        seit 02.10. aber schon am Artikelende und faehrt mit
                        dem Footer hoch; der Footer (z-1300) schob sich ueber
                        den stehengebliebenen CTA (gemessen: Karte fest bei
                        554–788 px, Footer-Oberkante 519). Jetzt: eine Huelle
                        ueber den ganzen Artikel (absolute, kein Layout-
                        Einfluss), darin die Haft-Ebene in Fensterhoehe — sie
                        loest sich mit der Buehne. overflow hidden beschneidet
                        das Herausgleiten an ihrer Unterkante. */}
                    {!editorMode && runningCtaImRahmen && runningCta && (
                        <div style={{ position: "absolute", inset: 0, zIndex: 1201, pointerEvents: "none" }}>
                            <div
                                style={{
                                    position: "sticky",
                                    top: 0,
                                    // dvh statt lvh (Jans iPhone-Screen 03.10.2026):
                                    // Unterkante = SICHTBARE Fensterkante. Mit lvh lag
                                    // das untere Stueck hinter der eingeblendeten
                                    // Browserleiste — vom Abstand der Karte zum Rand
                                    // blieb fast nichts. Loest sich, sobald der Footer
                                    // ins Bild kommt.
                                    height: bodyScroll ? "100dvh" : stableHeight,
                                    overflow: "hidden",
                                    pointerEvents: "none",
                                }}
                            >
                                {/* pointerEvents "none" statt "auto" (Jans Fund 27.08.2026):
                                    der Anker spannte das Streifen-Band als UNSICHTBAREN
                                    Klick-Fresser uebers untere Buehnenband — genau dort
                                    sitzt der START-Knopf des Covers ("START geht nicht,
                                    nur Scrollen"). Der Streifen regelt seine Klickbarkeit
                                    selbst (Root: zeigen ? auto : none) — ein Kind mit
                                    explizitem auto bleibt trotz none am Eltern klickbar. */}
                                <div
                                    style={{
                                        position: "absolute",
                                        left: 0,
                                        right: 0,
                                        bottom: 0,
                                        pointerEvents: "none",
                                        ...ueberlagerungMassstabStil(ueberlagerungMassstab),
                                    }}
                                >
                                    <RunningCtaStapel
                                        strips={runningCta}
                                        zeitQuelle={ctaZeitQuelle}
                                        onCtaAntwort={onCtaAntwort}
                                    />
                                </div>
                            </div>
                        </div>
                    )}
                    {/* NAV-BUTTONS: eigene Haft-Ebene als Geschwister im
                        Artikel — exakt die CTA-Bauform eine Ebene drüber
                        (sticky + Höhe 0, Klotz in Bühnenhöhe): fixed bricht
                        in der Reader-Hülle, und IN der Bühne lägen die Knöpfe
                        unter den HTML-Boxen (slide-container z-1000 schlägt
                        den sticky-Kontext z-998). z-1350: GANZ OBEN — über
                        Foto-Nachweis (1200), CTA-Streifen (1201) UND dem
                        Footer-Wrapper (1300 in ViewerChrome/Vorschau-Dialog).
                        Jans Fund 26.08.2026: der volle CTA-Balken lag ueber
                        den Knoepfen und schluckte die Klicks ("Nav runter geht
                        nicht"); die 1.0-Rangfolge (Nachweis ueber Buttons)
                        ist damit bewusst aufgegeben — Bedienbarkeit gewinnt.
                        Jans Fund 30.08.2026: mit z-1202 verschwand der
                        Hoch-Pfeil an den Credits UNTER dem Footer (#518
                        zeigte ihn dort, der Footer deckte ihn ab). */}
                    {navButtonsAn && navButtons && visualStoryStore && (
                        <div style={{ position: "sticky", top: 0, height: 0, zIndex: 1350 }}>
                            <div
                                style={{
                                    position: "relative",
                                    height: bodyScroll ? "100lvh" : stableHeight,
                                    pointerEvents: "none",
                                }}
                            >
                                {/* KAPITEL-PUNKTE rechts mittig (Jans Wunsch 06.10.2026):
                                    nur mit Nav-Knöpfen, in deren Farben, ohne sie zu
                                    berühren (unten 12 + 2 × 48 + 12 px, oben gleich viel) */}
                                <KapitelPunkte
                                    slides={slides}
                                    aktuellerSlide={currentSlideIndex}
                                    store={visualStoryStore}
                                    farbe={navButtons.arrowColor}
                                    grund={navButtons.bgColor}
                                    maxHoehe={(bodyScroll ? innerHeight : stableHeight) - 2 * 132}
                                    kapitelWort={kapitelWort}
                                />
                                <NavButtonsSpalte
                                    store={visualStoryStore}
                                    arrowColor={navButtons.arrowColor}
                                    bgColor={navButtons.bgColor}
                                    slideCount={slides.length}
                                    currentSlideIndex={currentSlideIndex}
                                />
                            </div>
                        </div>
                    )}
                    <div
                        style={{
                            // Beide Modi sticky. Seiten-Scroll war bis 02.10.2026
                            // `fixed`: die Buehne blieb dann auch nach dem letzten
                            // Bild am Bildschirm stehen und der Footer schob sich
                            // darueber (Benedikts Handy-Fund). sticky loest sich am
                            // Artikelende und zieht das letzte Bild mit dem Footer
                            // hoch, wie im Kasten-Scroll am Desktop. Damit die
                            // Seitenlaenge gleich bleibt, entfaellt dafuer im
                            // Seiten-Scroll der Platzhalter darunter (die Buehne
                            // belegt jetzt selbst die 100lvh im Fluss).
                            position: "sticky",
                            zIndex: 998,
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: bodyScroll ? "auto" : 0,
                            height: bodyScroll ? "100lvh" : `${stableHeight}px`,
                            background: scrollyBg,
                        }}
                    >
                        {/* GLOBALER LOOK (GLOBAL-Reiter im Pro-Editor).
                            Der Filter liegt auf der BUEHNE — Canvas und die
                            Ebenen darauf —, nicht auf den HTML-Textboxen: die
                            stehen als eigene Scroll-Abschnitte ausserhalb
                            dieses Containers (Jans Entscheidung 05.08.2026).
                            Ohne eingestellten Wert ist `filter` undefined; ein
                            wirkungsloser Filter waere nur ein zusaetzlicher
                            Kompositor-Layer. */}
                        <div style={{ position: "absolute", inset: 0, filter: look.filter }}>
                            <Renderer
                                canvasContainerRef={canvasContainerRef}
                                htmlContainerRef={htmlContainerRef}
                                editorMode={editorMode}
                            />
                        </div>
                        {/* Temperatur, Vignette und Korn NICHT im gefilterten
                            Container: erst das Bild abstimmen, dann die
                            Film-Anmutung darueber — sonst haette die Helligkeit
                            auch die Vignette aufgehellt. */}
                        <GlobalLookOverlay look={look} />
                        {/* EDITOR: der Nachweis gehoert IN den sticky-Container.
                            Er bleibt beim Scrollen stehen, also klebt der Nachweis
                            unten an der Buehne — genau wie `fixed` es im Reader
                            tut. Als Geschwister (unten) haengt er dagegen am
                            scrollenden Inhalt und wandert mit (Jans Fund
                            "der Foto-Credit bewegt sich, was nicht sein darf").
                            Der Stacking-Context-Grund fuer die Geschwister-
                            Loesung gilt nur im Reader: dort muss der Nachweis
                            ueber die NavigationControls (z-1101), die es im
                            Editor gar nicht gibt. */}
                        {/* IMMER gemountet, auch ohne Text: Sonst waere das
                            Ausblenden nicht zu sehen — der Knoten verschwaende
                            im selben Bild, in dem die Blende beginnt (Jans
                            Vorgabe 05.08.2026). Ohne Text ist er unsichtbar
                            und faengt nichts ab. */}
                        {/* AUCH IM VOLL-READER (Lauras WK-Fund 10.09.2026,
                            "Fotocredits werden immer wieder nicht angezeigt"):
                            Der Reader-Scroller traegt `contain: content`
                            (.scrolly-scroll-container, Safari-Scroll-Fix) und
                            wird damit zum Bezugsrahmen fuer `fixed` — der
                            Geschwister-Block unten haengt dann am ARTIKEL und
                            scrollt mit dem Inhalt davon (gemessen: y=-803 px
                            direkt nach dem Start, danach immer weiter oben).
                            Genau der Fehler, den der mitlaufende CTA am
                            08.08. hatte; dieselbe Loesung: in der Buehne
                            verankern, wenn `runningCtaImRahmen` gesetzt ist. */}
                        {(editorMode || runningCtaImRahmen) && (
                            <div
                                style={{
                                    position: "absolute",
                                    left: 0,
                                    right: 0,
                                    bottom: 0,
                                    zIndex: 1200,
                                    pointerEvents: "none",
                                    ...(runningCtaImRahmen ? ueberlagerungMassstabStil(ueberlagerungMassstab) : {}),
                                }}
                            >
                                <Credits
                                    light={creditsLight}
                                    text={creditsText}
                                    photographerUrl={creditsPhotographerUrl}
                                    weitere={creditsWeitere}
                                />
                            </div>
                        )}
                        {/* MITLAUFENDER CTA, EDITOR-Platz: wie der Nachweis IN
                            den sticky-Container — dort klebt er unten an der
                            BÜHNE. `fixed` scheitert im Editor am Interface-Zoom
                            (transform macht den Vorfahren zum Bezugsrahmen,
                            Begründung beim Reader-Credits-Block). Über dem
                            Nachweis (zIndex 1201): Der Streifen ist bedienbar,
                            der Nachweis nur Beschriftung. */}
                        {editorMode && runningCta && (
                            <div
                                style={{
                                    position: "absolute",
                                    left: 0,
                                    right: 0,
                                    bottom: 0,
                                    zIndex: 1201,
                                    // Kein Klick-Fresser bei unsichtbarem Streifen
                                    // (s. Reader-Anker, Jans Fund 27.08.2026).
                                    pointerEvents: "none",
                                }}
                            >
                                <RunningCtaStapel
                                    strips={runningCta}
                                    zeitQuelle={ctaZeitQuelle}
                                    editorMode
                                />
                            </div>
                        )}
                    </div>
                    {/* Credits footer — rendered as a SIBLING of the sticky
                        container (not as a child of Renderer) so it escapes
                        the sticky's z-index:998 stacking context. Nested
                        stacking contexts never let their inner z-index
                        escape, so the previous attempt (fixed inside the
                        sticky at zIndex 1200) was globally stuck behind
                        NavigationControls (fixed bottom-0 z-[1101]). At
                        this DOM level we share the same stacking context
                        as NavigationControls and can stack above it with
                        z-index 1200. */}
                    {/* READER-Variante. Im Editor uebernimmt der Block IM
                        sticky-Container oben (sonst wandert der Nachweis beim
                        Scrollen mit dem Inhalt statt an der Buehne zu kleben).
                        `fixed` funktioniert hier nur im Reader: im Editor traegt
                        der Root ein `transform: scale()` fuer den Interface-Zoom,
                        und ein transformierter Vorfahr wird zum Bezugsrahmen fuer
                        `fixed` — der Nachweis landete dann am unteren Rand des
                        GANZEN Editors, unter der Zeitleiste. */}
                    {/* Nur fuer Huellen OHNE `contain`/`transform` am Scroller
                        (Vorschau-Kacheln, Einbettungen). Der Voll-Reader setzt
                        runningCtaImRahmen und nimmt den Block IN der Buehne
                        oben (Begruendung dort). */}
                    {!editorMode && !runningCtaImRahmen && (
                        <div
                            style={{
                                position: "fixed",
                                bottom: 0,
                                left: 0,
                                right: 0,
                                zIndex: 1200,
                                pointerEvents: "none",
                            }}
                        >
                            <Credits
                                light={creditsLight}
                                text={creditsText}
                                photographerUrl={creditsPhotographerUrl}
                                    weitere={creditsWeitere}
                            />
                        </div>
                    )}
                    {/* MITLAUFENDER CTA, LESER-Platz: wie der Nachweis als
                        GESCHWISTER des sticky-Containers, `fixed` ans Fenster —
                        nur so steht er über den NavigationControls (fixed,
                        z-1101; die Stacking-Context-Begründung steht am
                        Credits-Block darüber). zIndex 1201: über dem Nachweis,
                        denn der Streifen ist bedienbar, der Nachweis nicht. */}
                    {!editorMode && !runningCtaImRahmen && runningCta && (
                        <div
                            style={{
                                position: "fixed",
                                bottom: 0,
                                left: 0,
                                right: 0,
                                zIndex: 1201,
                                // Kein Klick-Fresser bei unsichtbarem Streifen
                                // (s. Reader-Anker, Jans Fund 27.08.2026).
                                pointerEvents: "none",
                            }}
                        >
                            <RunningCtaStapel
                                strips={runningCta}
                                zeitQuelle={ctaZeitQuelle}
                                onCtaAntwort={onCtaAntwort}
                            />
                        </div>
                    )}
                    {/* Cover-Reservation-Spacer (2026-05-28): konstant stableHeight,
                        NICHT mehr `isAfterStory ? 0 : stableHeight`-toggle.

                        Hintergrund: vorher schaltete der spacer beim afterStory-Latch
                        (lastSlide passiert) auf 0 → article-container.scrollHeight
                        reduzierte sich um stableHeight → Browser clampt scrollTop →
                        sichtbarer Sprung nach oben am Story-Ende ("End-Snap",
                        Bene-Befund 2026-05-28).

                        Außerdem reservierte der spacer im Stage-Gating-Pattern
                        (ViewerChrome mountet ScrollyViewer erst nach Cover-Klick)
                        einen Cover-shaped leeren Bereich nach dem Cover-Ext-Mount,
                        was visuell wie ein "zweites Cover" wirkte
                        ("Cover-Doppelmount", gleicher Befund).

                        Fix: konstanter spacer = stableHeight. Layout-Distance bleibt
                        identisch über die ganze Story-Dauer. afterStory-Subscription
                        bleibt im Store (wird ggf. von anderer Logik konsumiert,
                        Master-Suche zeigt aber nur diese Stelle als Konsument). */}
                    <div style={{ height: bodyScroll ? 0 : stableHeight }} />
                    <div
                        style={{
                            position: "relative",
                            top: -stableHeight,
                            // Editor (Pro-Editor): die Scroll-Sektionen (.slide-container /
                            // .slide-content) liegen mit pointer-events:auto über dem Canvas
                            // und schlucken Gizmo-/Layer-Klicks. Der Editor scrubbt über die
                            // Timeline (nicht über Scroll), daher hier durchlässig schalten —
                            // Text bleibt sichtbar, Selektion läuft via Canvas-Raycasting.
                            // Reader (editorMode=false) unverändert.
                            ...(editorMode ? { pointerEvents: "none" as const } : {}),
                        }}
                    >
                        {slides.map((slide, i) => {
                            // A-22 (sister of A-4): guard the presentationOptions
                            // fallback so a slide without per-slide options on a
                            // scrolly without presentationOptions renders instead of
                            // crashing the shared renderer. Empty-object fallback is
                            // safe: the color helpers below already treat undefined
                            // backgroundColor/fontColor as "no color".
                            const options =
                                slide.options ||
                                post.data?.data?.presentationOptions?.slideOptions ||
                                ({} as SlideOptionsSerialized);
                            const previousSlide = slides[i - 1];
                            let animateAreaHeight = 0;
                            if (previousSlide) {
                                const animationDuration = Math.max(0, slide.from - previousSlide.from);
                                const factor = Math.max(animationDuration / 1000, 1);
                                animateAreaHeight = stableHeight / 2 + stableHeight * factor;
                            } else if (slide.from > 0) {
                                // First slide with from > 0: add initial spacer so the
                                // text doesn't appear before the user has scrolled past
                                // the start screen.
                                const factor = Math.max(slide.from / 1000, 1);
                                animateAreaHeight = stableHeight / 2 + stableHeight * factor;
                            }
                            const multiplier = responsiveMultiplier(innerWidth);
                            const showText = slide.text?.show ?? true;
                            const padding = options.padding * multiplier;
                            const borderRadius = options.borderRadius * multiplier;
                            const margin = options.margin * multiplier;
                            const companyBoxPosition = options.companyBoxPosition;
                            /**
                             * Die zwei Pro-Editor-Regler fuer die Textbox
                             * (Jans Fund 23.09.2026: "50 % und mittig" sah nur
                             * im Pro-Editor richtig aus).
                             *
                             * Der Pro-Editor rechnet die Box-Lage in seiner
                             * EIGENEN Fassung von calculateSlidePosition und
                             * schreibt dabei `boxHorizontalAlign` und
                             * `boxWidth` in die Slide-Optionen. Der
                             * veroeffentlichte Reader kannte bis hierher nur
                             * das alte, scrolly-weite `companyBoxPosition` —
                             * beide Regler kamen also nie an (gemessen an
                             * welcome-to-the-pro-editor: boxHorizontalAlign
                             * "center" und boxWidth 1920 in den Daten,
                             * companyBoxPosition gar nicht vorhanden → links).
                             *
                             * Vorgaben aendern nichts: `auto` faellt auf das
                             * bisherige Verhalten zurueck, `0` auf die
                             * bisherige Breite. Auf schmalen Schirmen bleibt
                             * die Box voll breit — dort hat der Pro-Editor
                             * dieselbe Ausnahme (useMobile).
                             */
                            const schmal = innerWidth <= 850;
                            const boxAnker = (options as { boxHorizontalAlign?: string })
                                .boxHorizontalAlign;
                            const ankerGesetzt = !schmal && !!boxAnker && boxAnker !== "auto";
                            const boxBreitePx = (options as { boxWidth?: number }).boxWidth;
                            // Der Wert steht als Pixel auf 3840 px Entwurfsbreite —
                            // derselbe Massstab wie im Pro-Editor.
                            const ENTWURFSBREITE = 3840;
                            const festeBreite =
                                !schmal && boxBreitePx && boxBreitePx > 0
                                    ? (boxBreitePx / ENTWURFSBREITE) * innerWidth
                                    : undefined;
                            // Unschaerfe/Schlagschatten aus BEIDEN Quellen: der
                            // Template-Editor setzt sie an der Slide, der
                            // Pro-Editor in den Optionen. Frueher las der Reader
                            // nur die Slide — die Haken im Pro-Editor kamen im
                            // veroeffentlichten Scrolly also nie an (box-effects.ts).
                            const boxEffects = resolveBoxEffects(slide, options);
                            return (
                                <div
                                    key={slide.id}
                                    className="slide-container"
                                    style={{
                                        opacity: isSlideTextEmpty(slide.text?.text) ? 0 : 1,
                                        // EDITOR (Pro-Editor): Jans eigener
                                        // TextRenderer/TextOverlayNormal zeichnet die
                                        // Textfenster und positioniert sie aus dem
                                        // PLAYHEAD. Diese hier haengen dagegen an der
                                        // SCROLL-Position — im Editor wird nicht mehr
                                        // gescrollt, sie blieben also unter der Buehne
                                        // liegen. Beide gleichzeitig sichtbar hiess
                                        // frueher doppelter, gegeneinander jitternder
                                        // Text. `visibility` statt `display`/Weglassen:
                                        // das Layout bleibt erhalten, damit
                                        // VisualStoryStore#initialisePositions weiter
                                        // echte Rechtecke misst (es liest
                                        // .slide-container/.slide-content aus dem DOM).
                                        // Reader unveraendert.
                                        ...(editorMode ? { visibility: "hidden" as const } : {}),
                                        ...(fontFamilies.textFontFamily
                                            ? { fontFamily: fontFamilies.textFontFamily }
                                            : {}),
                                    }}
                                >
                                    <div
                                        className="slide-animate"
                                        style={{ height: animateAreaHeight, pointerEvents: "none" }}
                                    />
                                    {showText && !isSlideTextEmpty(slide.text?.text) && (
                                        <div
                                            style={{
                                                padding: margin,
                                                display: "flex",
                                                justifyContent: ankerGesetzt
                                                    ? boxAnker === "center"
                                                        ? "center"
                                                        : boxAnker === "right"
                                                          ? "flex-end"
                                                          : "flex-start"
                                                    : companyBoxPosition === "center"
                                                      ? "center"
                                                      : companyBoxPosition === "right"
                                                        ? "flex-end"
                                                        : "flex-start",
                                            }}
                                        >
                                            <div
                                                className="slide-content"
                                                style={{
                                                    // Background color resolution:
                                                    //  - No blur: solid color (from branding merge
                                                    //    or per-slide override)
                                                    //  - Blur on: same color, but alpha capped at
                                                    //    0.85 so the backdrop blur is visible behind
                                                    //    the tint. Without the cap an opaque color
                                                    //    would block the blur entirely and users
                                                    //    would think "Blur" is broken.
                                                    //  - No color at all + blur: legacy white-
                                                    //    translucent fallback for older PPI templates.
                                                    background: tintBackgroundForBlur(
                                                        options.backgroundColor,
                                                        boxEffects.blur,
                                                    ),
                                                    color: getColorString(options.fontColor) || undefined,
                                                    padding,
                                                    borderRadius,
                                                    // Feste Box-Breite aus dem Pro-Editor;
                                                    // nie breiter als die Spalte.
                                                    ...(festeBreite
                                                        ? { width: festeBreite, maxWidth: "100%" }
                                                        : {}),
                                                    // Faktor fuer feste Pixel-Schriftgroessen im
                                                    // Text (s. MarkdownViewer) — derselbe wie fuer
                                                    // Innenabstand, Radius und Grundschrift.
                                                    ...({ "--scrolly-box-skala": multiplier } as React.CSSProperties),
                                                    // Link-Design dieser Box (eigene Optionen der
                                                    // Slide, sonst die des Scrollys)
                                                    ...(linkStilVariablen(
                                                        // slideOpts: beide Schreibweisen (s. oben)
                                                        linkStilAus(slide.options, slideOpts),
                                                    ) as React.CSSProperties),
                                                    // Apply the backdrop filter whenever blur is
                                                    // on — the alpha cap above guarantees the blur
                                                    // is visible through the tint.
                                                    ...(boxEffects.blur
                                                        ? { backdropFilter: "blur(12px)", WebkitBackdropFilter: "blur(12px)" }
                                                        : {}),
                                                    ...(boxEffects.shadow
                                                        // Zweischichtig wie im Editor-Fenster (dort als
                                                        // drop-shadow @0.45 + @0.25, RendererWebGL) — der
                                                        // frueher einschichtige @0.25 liess veroeffentlichte
                                                        // Boxen heller wirken als im Editor (PR #227-Notiz).
                                                        ? { boxShadow: "0 8px 32px rgba(0,0,0,0.45), 0 2px 8px rgba(0,0,0,0.25)" }
                                                        : {}),
                                                }}
                                            >
                                                <MarkdownViewer source={slide.text?.text} />
                                                {/* Call-to-Action am unteren Rand der Box
                                                    (Jans Auftrag 05.08.2026). Sichtbarkeit und
                                                    Ziel entscheidet `resolveCta` — dieselbe
                                                    Regel wie im Editor. */}
                                                {(() => {
                                                    const cta = resolveCta(
                                                        (slide as { cta?: CtaConfig | null }).cta,
                                                    );
                                                    return cta ? (
                                                        <CtaButton cta={cta} editorMode={editorMode} />
                                                    ) : null;
                                                })()}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                    {/* NACHLAUF, wenn auf der letzten Slide ein CTA steht (s. endeVerweilen) */}
                    {endeVerweilen > 0 && <div style={{ height: endeVerweilen }} />}
                </div>
            </div>
        </>
    );
}
